import { useAthenaLogger, useConfig, useSession } from '..';
import { useTransmitRef } from '../../../src/contexts/transmitContext';
import axios, { AxiosInstance } from 'axios';
import TransmitToken from '../../models/TransmitToken';
import {
  Destination,
  RefreshAccessTokenOptions,
  SessionPayload,
  TicketPayload,
} from '@ally/transmitigator';
import { clearCookies } from '../../utils/CookieUtil';
import {
  deleteLocalStorage,
  deleteSessionStorage,
} from '../../utils/StorageUtil';
import { makeGuid } from '../../utils';
import { useStore } from '../../store/store';

const useTransmit = () => {
  const { state, dispatch } = useStore();
  const {
    investConfig: { bankUrl },
  } = useConfig();
  const { transmitRef, transmitToken, setTransmitToken } = useTransmitRef();
  const { ticketID } = transmitRef.useTransmit();
  const { wealthConfig } = useConfig();
  const proxyInstance: AxiosInstance = axios.create({
    baseURL: wealthConfig.proxyUrl,
    withCredentials: true,
  });

  const journeyVersion = 'v2';

  const athenaLogger = useAthenaLogger();

  const initTransmit = async (haveSession: boolean = false): Promise<void> => {
    try {
      const cachedTransmitToken = state.transmitToken;
      if (cachedTransmitToken) {
        await setTransmitToken(cachedTransmitToken);
      }
      // Pass the ticketID from the URL into rehydrate() so the SDK can
      // exchange it for an access token. Without this, rehydrate() sees
      // ticketID=undefined and returns null immediately for new navigations.
      const urlTicketID =
        new URLSearchParams(window.location.search).get('ticketID') ??
        undefined;
      const response = (await transmitRef.rehydrate(
        urlTicketID ? { ticketID: urlTicketID } : undefined
      )) as SessionPayload;
      if (!response) {
        throw new Error('rehydrade - no response');
      }
      const { access_token, userNamePvtEncrypt } = response.session;
      if (!access_token) {
        throw new Error('no access token');
      }
      const newToken = new TransmitToken(access_token, userNamePvtEncrypt);
      await setTransmitToken(newToken);
      await dispatch({ type: 'SET_TRANSMIT_TOKEN', payload: newToken });
    } catch (error: any) {
      athenaLogger.sendLog(
        `*TRANSMIT* initTransmit Error: Session Data Exists ? ${sessionStorage.getItem(
          'username'
        )} - ${error.message || 'Unknown error'}`
      );
      window.location.href = bankUrl;
    }
  };

  const getTransferTicket = async (journey_destination: Destination) => {
    try {
      return await transmitRef.transferTicket({
        destination: journey_destination,
        access_token: transmitToken.access_token,
        username: transmitToken.userNamePvtEncrypt,
        journey_version: journeyVersion,
      });
    } catch (error: any) {
      athenaLogger.sendLog(
        `*TRANSMIT* getTransferTicket Error: ${
          error.message || 'Unknown error'
        }`
      );
    }
  };

  const appendTicketID = (ticketID: any, destination: string) => {
    let parsedUrl = new URL(destination);

    if (ticketID) {
      if (parsedUrl.searchParams.has('relayState')) {
        parsedUrl = new URL(parsedUrl.searchParams.get('relayState') || '');
      }
      parsedUrl.searchParams.append('ticketID', ticketID);
    }

    return parsedUrl.toString();
  };

  const leaveWithTicket = async (
    e: React.MouseEvent<Element, MouseEvent>,
    destination: string,
    journey_destination: Destination = 'wealth'
  ) => {
    try {
      e.preventDefault();
      let url = destination;
      if (transmitToken?.access_token) {
        const { ticketID } = (await getTransferTicket(
          journey_destination
        )) as TicketPayload;
        url = appendTicketID(ticketID, destination);
        clearCookies();
        await proxyInstance.get('/identity/logout');
        deleteLocalStorage('sessionExpiry');
        deleteSessionStorage('WEALTH-TS');
      }
      window.location.href = url;
    } catch (error: any) {
      athenaLogger.sendLog(
        `*TRANSMIT* leaveWithTicket Error: ${error.message || 'Unknown error'}`
      );
    }
  };

  const refreshTransmitToken = async () => {
    try {
      if (!transmitToken?.access_token) {
        throw new Error('no transmit token');
      }

      if (!transmitRef) {
        throw new Error('no transmit ref');
      }

      const { expirationDate } = transmitToken;

      const expiry = new Date(expirationDate || 0);
      const currentDate = new Date();
      const ninetySecondsFromNow = new Date(currentDate.getTime() + 90000);
      if (!expirationDate || expiry > ninetySecondsFromNow) {
        return;
      }

      const options: RefreshAccessTokenOptions = {
        access_token: transmitToken.access_token || '',
        username: transmitToken.userNamePvtEncrypt || '',
        journey_version: journeyVersion,
      };

      const response = (await transmitRef.refreshAccessToken(
        options
      )) as SessionPayload;

      const { access_token } = response?.session || undefined;

      if (!access_token) {
        throw new Error('no access token');
      }

      const newTransmitToken = new TransmitToken(
        access_token,
        transmitToken.userNamePvtEncrypt
      );

      setTransmitToken(newTransmitToken);
      await proxyInstance.post(
        '/transmit-token/update',
        { transmitToken: newTransmitToken },
        {
          headers: {
            'Content-Type': 'application/json',
            correlationid: makeGuid(),
          },
        }
      );
    } catch (error: any) {
      athenaLogger.sendLog(
        `*TRANSMIT* refreshTransmitToken Error: ${error.message || 'Unknown'}`
      );
    }
  };

  const logoutTransmit = async () => {
    await transmitRef.logout();
  };

  return {
    ticketID,
    initTransmit,
    leaveWithTicket,
    logoutTransmit,
    refreshTransmitToken,
  };
};

export default useTransmit;
